import { useEffect, useState } from "react";
import { CometChat } from "@cometchat/chat-sdk-javascript";
import { CometChatUIKitLoginListener } from "@cometchat/chat-uikit-react";
import { useChannels, useGroups, useDirectMessages, Selection } from "./useChatData";
import { initials, avatarColorClass } from "./format";
import { ChevronIcon } from "./Icons";
import AvatarHoverCard from "./AvatarHoverCard";
import NewChatModal from "./NewChatModal";
import { userScopedKey } from "./later";
import { GROUP_REMOVED_EVENT } from "./groupCleanup";
import { fetchDirectoryScope } from "./directoryScope";
import { loadOpenedDms, OPENED_DMS_EVENT, OpenedDm } from "./openedDms";
import DirectoryTree from "./DirectoryTree";
import Avatar from "./Avatar";
import { seedAvatarFromUser } from "./avatars";
import { seedNameFromUser } from "./names";
import * as sounds from "./sounds";

type Props = {
  selection: Selection | null;
  onSelect: (s: Selection) => void;
  onOpenProfile?: (uid: string) => void;
};

const FAV_STORAGE_KEY = "dbm-chat-favourites";

function loadFavourites(): Set<string> {
  try {
    const raw = localStorage.getItem(userScopedKey(FAV_STORAGE_KEY));
    return new Set(raw ? (JSON.parse(raw) as string[]) : []);
  } catch {
    return new Set();
  }
}

/** Local, per-browser favourites — starred items pinned to the top,
 * namespaced per logged-in uid (shared browsers don't cross-pollinate). */
function useFavourites() {
  const [favourites, setFavourites] = useState<Set<string>>(loadFavourites);

  useEffect(() => {
    localStorage.setItem(userScopedKey(FAV_STORAGE_KEY), JSON.stringify(Array.from(favourites)));
  }, [favourites]);

  const toggle = (key: string) => {
    setFavourites((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // A starred channel/group you've left, been kicked from, or that got
  // deleted shouldn't keep sitting in "Starred" as a dead link back in.
  useEffect(() => {
    const onRemoved = (e: Event) => {
      const guid = (e as CustomEvent<{ guid: string }>).detail?.guid;
      if (!guid) return;
      setFavourites((prev) => {
        const key = `group:${guid}`;
        if (!prev.has(key)) return prev;
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    };
    window.addEventListener(GROUP_REMOVED_EVENT, onRemoved);
    return () => window.removeEventListener(GROUP_REMOVED_EVENT, onRemoved);
  }, []);

  return { favourites, toggle };
}

type Row = {
  key: string;
  selection: Selection;
  active: boolean;
  unread?: number;
  render: () => JSX.Element;
};

export default function Sidebar({ selection, onSelect, onOpenProfile }: Props) {
  const { groups, loading: loadingGroups, refetch: refetchChannels, addLocal: addLocalChannel } = useChannels();
  const { groups: dbmGroups, loading: loadingDbmGroups, refetch: refetchGroups, addLocal: addLocalGroup } = useGroups();
  const [showNewChat, setShowNewChat] = useState(false);
  const {
    conversations,
    loading: loadingDms,
    presence: dmPresence,
    unreadOverrides,
  } = useDirectMessages();
  const { favourites, toggle: toggleFavourite } = useFavourites();
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  /* Two zones rather than nine stacked sections: 1-4 are conversations,
   * the Directory is the whole org. Mixed together, a large External list
   * pushes your own DMs off the screen. */
  const [zone, setZone] = useState<"chats" | "directory">("chats");
  // Only internal team members get a directory at all. canSeeAll is the
  // right test rather than category === "team": it's also true before any
  // domain has been classified, when nobody is restricted yet.
  const [canBrowse, setCanBrowse] = useState(false);
  useEffect(() => {
    let cancelled = false;
    fetchDirectoryScope()
      .then((s) => {
        if (!cancelled) setCanBrowse(s.canSeeAll);
      })
      .catch(() => {
        // Leave the directory hidden — a failed lookup shouldn't hand an
        // external user a people list they aren't meant to browse.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // People opened from search but not yet messaged — see openedDms.ts.
  const [openedDms, setOpenedDms] = useState<OpenedDm[]>(loadOpenedDms);
  useEffect(() => {
    const sync = () => setOpenedDms(loadOpenedDms());
    window.addEventListener(OPENED_DMS_EVENT, sync);
    return () => window.removeEventListener(OPENED_DMS_EVENT, sync);
  }, []);

  // Channel/group membership changed elsewhere (left/deleted in details).
  useEffect(() => {
    const bump = () => {
      refetchChannels();
      refetchGroups();
    };
    window.addEventListener("dbm-channels-changed", bump);
    return () => window.removeEventListener("dbm-channels-changed", bump);
  }, [refetchChannels, refetchGroups]);

  const isOn = (kind: Selection["kind"], id: string) =>
    selection?.kind === kind && selection.id === id;
  const toggleSection = (key: string) =>
    setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }));

  // ---- Build a uniform row list per section, so Favourites can borrow from any of them ----
  const channelRows: Row[] = groups.map((g) => {
    const id = g.getGuid();
    const key = `group:${id}`;
    const sel: Selection = {
      kind: "group",
      id,
      name: g.getName(),
      subtitle: g.getType?.() === "private" ? "Private channel" : "Channel",
      membersCount: g.getMembersCount?.(),
      isStructuredChannel: true,
    };
    return {
      key,
      selection: sel,
      active: isOn("group", id),
      render: () => (
        <>
          <span className="hash">#</span>
          <span className="nm">{g.getName()}</span>
        </>
      ),
    };
  });

  const groupRows: Row[] = dbmGroups.map((g) => {
    const id = g.getGuid();
    const key = `group:${id}`;
    const sel: Selection = {
      kind: "group",
      id,
      name: g.getName(),
      subtitle: "Group",
      membersCount: g.getMembersCount?.(),
      isStructuredChannel: false,
    };
    return {
      key,
      selection: sel,
      active: isOn("group", id),
      render: () => (
        <>
          <span className={`dav ${avatarColorClass(id)}`}>{initials(g.getName())}</span>
          <span className="nm">{g.getName()}</span>
        </>
      ),
    };
  });

  const dmRows: Row[] = conversations.map((c) => {
    const other = c.getConversationWith() as CometChat.User;
    // These already carry the avatar — seeding avoids a per-uid lookup.
    seedAvatarFromUser(other);
    seedNameFromUser(other);
    const id = other.getUid();
    const key = `user:${id}`;
    const unread = unreadOverrides[id] ?? ((c.getUnreadMessageCount?.() as number) || 0);
    // The live map (updated straight from the presence event) wins once
    // we've heard anything for this uid; the conversation's own snapshot
    // is only the fallback for the very first render, before any event
    // has arrived.
    const online = dmPresence[id] ?? other.getStatus?.() === "online";
    return {
      key,
      selection: { kind: "user", id, name: other.getName(), subtitle: "Direct message" },
      active: isOn("user", id),
      unread,
      render: () => (
        <>
          <AvatarHoverCard uid={id} name={other.getName()} onOpenProfile={onOpenProfile}>
            <Avatar uid={id} name={other.getName()} size="dav" />
          </AvatarHoverCard>
          <span className="nm">{other.getName()}</span>
          {/* "Offline", not "Away" — see the DM header in MessagePane:
              CometChat presence is binary, so "Away" overstated what a
              missing socket actually tells us. */}
          <span className={`st-inline ${online ? "onl" : "awy"}`} title={online ? "Online" : "Offline"} />
        </>
      ),
    };
  });

  /* Notes to self, the way Teams and WhatsApp do it — a normal DM with
   * yourself, pinned to the top of the list so it's always one click
   * away. Only the row is special-cased; everything downstream treats it
   * as an ordinary conversation. */
  const me = CometChatUIKitLoginListener.getLoggedInUser();
  const meUid = me?.getUid();
  const selfName = (me?.getName() || "").split(/\s+/)[0] || "You";
  const selfRow: Row[] = meUid
    ? [
        {
          key: `user:${meUid}`,
          selection: {
            kind: "user",
            id: meUid,
            name: me?.getName() || selfName,
            subtitle: "Notes to self",
          },
          active: isOn("user", meUid),
          render: () => (
            <>
              <Avatar uid={meUid} name={me?.getName() || selfName} size="dav" />
              <span className="nm">
                {selfName} <span className="dm-self">(you)</span>
              </span>
            </>
          ),
        },
      ]
    : [];

  /* Someone you've opened but not yet messaged has no CometChat
   * conversation to list, so they'd disappear from the sidebar the moment
   * you clicked away. For an external user — whose only route to anyone is
   * search — that's the difference between having an inbox and not. */
  const conversationUids = new Set(
    conversations.map((c) => (c.getConversationWith() as CometChat.User).getUid())
  );
  const pendingDmRows: Row[] = openedDms
    // The self row is rendered separately above, so skip it here — being
    // remembered as "opened" would otherwise list you twice.
    .filter((o) => o.uid !== meUid && !conversationUids.has(o.uid))
    .map((o) => ({
      key: `user:${o.uid}`,
      selection: { kind: "user" as const, id: o.uid, name: o.name, subtitle: "Direct message" },
      active: isOn("user", o.uid),
      render: () => (
        <>
          <AvatarHoverCard uid={o.uid} name={o.name} onOpenProfile={onOpenProfile}>
            <Avatar uid={o.uid} name={o.name} size="dav" />
          </AvatarHoverCard>
          <span className="nm">{o.name}</span>
        </>
      ),
    }));
  // Self first, then real conversations, then opened-but-unmessaged.
  const allDmRows = [
    ...selfRow,
    ...dmRows.filter((r) => r.selection.id !== meUid),
    ...pendingDmRows,
  ];

  const allRows = [...channelRows, ...groupRows, ...allDmRows];
  const favouriteRows = allRows.filter((r) => favourites.has(r.key));

  // Total unread across direct messages — the single number the parent
  // portal's floating chat bubble badge needs, posted up whenever it changes.
  const totalUnreadDms = dmRows.reduce((sum, r) => sum + (r.unread || 0), 0);
  useEffect(() => {
    window.parent.postMessage({ type: "DBM_CHAT_UNREAD_COUNT", count: totalUnreadDms }, "*");
  }, [totalUnreadDms]);

  const renderRow = (row: Row) => (
    <div key={row.key} className={`item ${row.unread ? "unread" : ""} ${row.active ? "on" : ""}`}>
      <button className="item-star" onClick={() => toggleFavourite(row.key)} title="Favourite" aria-label="Toggle favourite">
        <span className={favourites.has(row.key) ? "starred" : ""}>★</span>
      </button>
      <button
        className="item-main"
        onClick={() => {
          sounds.playOnce(sounds.SOUND.click);
          onSelect(row.selection);
        }}
      >
        {row.render()}
        {!!row.unread && <span className="cnt">{row.unread}</span>}
      </button>
    </div>
  );

  const openNewChat = () => {
    sounds.playOnce(sounds.SOUND.click);
    setShowNewChat(true);
  };

  /* `addLabel` puts a "+" on the section's own header. Creating a channel
   * or a group belongs next to the list it lands in — up in the workspace
   * header (where this used to live) it read as a generic action with no
   * hint of what it would create. */
  const section = (
    key: string,
    label: string,
    rows: Row[],
    loading: boolean,
    emptyLabel: string,
    addLabel?: string
  ) => (
    <div className="sect">
      <div className="sect-hrow">
        <button className="sect-h" onClick={() => toggleSection(key)}>
          <span className={`sect-caret ${collapsed[key] ? "is-collapsed" : ""}`}>
            <ChevronIcon size={11} />
          </span>
          {label}
        </button>
        {addLabel && (
          <button className="sect-add" title={addLabel} aria-label={addLabel} onClick={openNewChat}>
            +
          </button>
        )}
      </div>
      {!collapsed[key] && (
        <>
          {loading && <div className="sect-empty">Loading…</div>}
          {!loading && rows.length === 0 && <div className="sect-empty">{emptyLabel}</div>}
          {rows.map(renderRow)}
        </>
      )}
    </div>
  );

  return (
    <aside className="side">
      <div className="ws-head">
        <div className="wsname">
          <b>DBM Partner Portal</b>
          <span>discountbuildingmaterial.com.au</span>
        </div>
      </div>

      {canBrowse && (
        <div className="zoneswitch" role="tablist">
          <button
            role="tab"
            aria-selected={zone === "chats"}
            className={zone === "chats" ? "on" : ""}
            onClick={() => setZone("chats")}
          >
            Chats
          </button>
          <button
            role="tab"
            aria-selected={zone === "directory"}
            className={zone === "directory" ? "on" : ""}
            onClick={() => setZone("directory")}
          >
            Directory
          </button>
        </div>
      )}

      <div className="nav">
        {zone === "chats" || !canBrowse ? (
          <>
            {favouriteRows.length > 0 &&
              section("favourites", "Starred", favouriteRows, false, "")}
            {section("channels", "Channels", channelRows, loadingGroups, "No channels yet", "New channel")}
            {section("groups", "Groups", groupRows, loadingDbmGroups, "No groups yet", "New group")}
            {section(
              "dms",
              "Direct messages",
              allDmRows,
              loadingDms,
              canBrowse ? "No direct messages" : "Search above to start a chat"
            )}
          </>
        ) : (
          <DirectoryTree
            selection={selection}
            onSelect={onSelect}
            onOpenProfile={onOpenProfile}
          />
        )}
      </div>

      {/* Live huddle strip — static placeholder until huddles land (R2). */}
      <div className="huddle">
        <span className="pulse" aria-hidden="true" />
        <div className="h-meta">
          <b>No active huddle</b>
          <span>Start one from a channel</span>
        </div>
      </div>

      {showNewChat && (
        <NewChatModal
          onClose={() => setShowNewChat(false)}
          onSelect={onSelect}
          onChannelCreated={(group, kind) => {
            // Optimistic insert so it's visible instantly, plus a refetch
            // as a background consistency check.
            if (kind === "channel") {
              addLocalChannel(group);
              refetchChannels();
            } else {
              addLocalGroup(group);
              refetchGroups();
            }
          }}
        />
      )}
    </aside>
  );
}
